import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";

// Tile-cartogram coordinates (row, col) for each U.S. state — same layout NPR / FiveThirtyEight use.
// Reads density better than a real geographic map (no tiny NE states squashed together).
const TILE_GRID = [
  ["AK", null, null, null, null, null, null, null, null, null, "ME"],
  [null, null, null, null, null, null, null, null, null, "VT", "NH"],
  ["WA", "ID", "MT", "ND", "MN", "IL", "WI", "MI", null, "NY", "MA"],
  ["OR", "NV", "WY", "SD", "IA", "IN", "OH", "PA", "NJ", "CT", "RI"],
  ["CA", "UT", "CO", "NE", "MO", "KY", "WV", "VA", "MD", "DE", null],
  [null, "AZ", "NM", "KS", "AR", "TN", "NC", "SC", "DC", null, null],
  ["HI", null, "TX", "OK", "LA", "MS", "AL", "GA", null, null, null],
  [null, null, null, null, null, null, null, "FL", null, null, null],
];

// Density buckets (color stops) — chosen to surface the gap between empty and well-covered states
function bucketColor(count) {
  if (count === 0 || count === undefined) return "bg-zinc-100 text-zinc-400 border-zinc-200";
  if (count < 3) return "bg-amber-100 text-zinc-700 border-amber-300";
  if (count < 6) return "bg-amber-300 text-zinc-900 border-amber-500";
  if (count < 10) return "bg-orange-400 text-zinc-900 border-orange-600";
  return "bg-red-600 text-white border-red-800";
}

function bucketLabel(count) {
  if (count === 0 || count === undefined) return "0 records";
  if (count < 3) return "1–2 records";
  if (count < 6) return "3–5 records";
  if (count < 10) return "6–9 records";
  return "10+ records";
}

export default function CoverageMap() {
  const [stats, setStats] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    api.get("/abusers/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const counts = {};
  let total = 0;
  if (stats?.by_state) {
    for (const s of stats.by_state) {
      counts[s.state_code] = s.count;
      total += s.count;
    }
  }

  return (
    <section className="bg-white" data-testid="coverage-map" id="coverage-map">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="label-eyebrow">Registry coverage</div>
            <h2 className="font-display font-black text-3xl tracking-tight mt-1">
              Where we have records
            </h2>
          </div>
          {stats && (
            <div className="text-right">
              <div className="font-display font-black text-3xl tracking-tighter text-red-600">{total}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                records nationwide
              </div>
            </div>
          )}
        </div>

        <p className="text-sm text-zinc-600 max-w-2xl mb-6 leading-relaxed">
          Each square is a U.S. state, positioned roughly where it sits on the map.
          Darker = more convicted records. Light squares are gaps — let us know if you have leads.
        </p>

        {/* Tile cartogram */}
        <div className="border border-zinc-200 bg-zinc-50 p-3 sm:p-5 overflow-x-auto" data-testid="coverage-grid">
          <div className="inline-block">
            {TILE_GRID.map((row, ri) => (
              <div key={ri} className="flex gap-1.5 mb-1.5">
                {row.map((code, ci) => {
                  if (!code) {
                    return <div key={ci} className="w-10 h-10 sm:w-12 sm:h-12" aria-hidden="true" />;
                  }
                  const count = counts[code] || 0;
                  return (
                    <button
                      type="button"
                      key={ci}
                      onMouseEnter={() => setHovered({ code, count })}
                      onMouseLeave={() => setHovered(null)}
                      onFocus={() => setHovered({ code, count })}
                      onBlur={() => setHovered(null)}
                      className={`relative w-10 h-10 sm:w-12 sm:h-12 border ${bucketColor(count)} flex flex-col items-center justify-center transition-transform hover:scale-110 hover:z-10 focus:scale-110 focus:outline-none focus:ring-2 focus:ring-zinc-900`}
                      data-testid={`coverage-tile-${code}`}
                      aria-label={`${code}: ${count} records`}
                    >
                      <span className="font-display font-black text-[10px] sm:text-xs leading-none">{code}</span>
                      <span className="text-[9px] font-mono mt-0.5 leading-none">{count}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Hover detail + legend */}
        <div className="mt-4 flex items-center gap-4 flex-wrap justify-between">
          <div className="text-xs font-mono text-zinc-700 min-h-[1.25rem]" data-testid="coverage-hover">
            {hovered ? (
              <>
                <span className="font-bold">{hovered.code}</span>
                {" · "}
                {hovered.count === 0
                  ? "no records yet — gap"
                  : `${hovered.count} convicted record${hovered.count === 1 ? "" : "s"}`}
              </>
            ) : (
              <span className="text-zinc-400">Hover any state for details</span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-widest">
            {[
              { count: 0, label: "0" },
              { count: 1, label: "1–2" },
              { count: 3, label: "3–5" },
              { count: 6, label: "6–9" },
              { count: 10, label: "10+" },
            ].map((b) => (
              <span key={b.label} className="inline-flex items-center gap-1">
                <span className={`inline-block w-3 h-3 border ${bucketColor(b.count)}`} aria-hidden="true" />
                {b.label}
              </span>
            ))}
          </div>
        </div>

        <p className="mt-6 text-xs text-zinc-500 max-w-2xl leading-relaxed">
          Have a documented case we missed? <Link to="/about" className="underline hover:text-red-600">
          Submit a tip on the About page
          </Link> — we verify every submission against a primary source before publishing.
        </p>
      </div>
    </section>
  );
}
